<template>
  <div id='design-portfolio-inputs' class='card'>
    <div class='card-content'>
      <b-field
        message='Starting portfolio must have at least one ETF or mutual fund'
        class='border-override'>
        <template #label>
          <span class='label'>Your Starting Portfolio</span>
        </template>

        <b-autocomplete
          v-model='portfolioQuery'
          :data='portfolios'
          field='name'
          :loading='portfoliosLoading'
          open-on-focus
          clearable
          @select='selectPortfolio'>

          <template slot-scope='props'>
              <span>{{ props.option.name }}</span>
              <i v-if="props.option.portfolio_type === 'shareowner'" class='fas fa-user ml-1'></i>
          </template>
        </b-autocomplete>
      </b-field>


      <div class='input-row'>
        <b-field>
          <template #label>
            <span class='label'>Select Cause Areas</span>
          </template>

          <b-taginput
            v-model='selectedValues'
            :data='filteredValueSets'
            @typing='getFilteredValueSets'
            autocomplete
            field='name'
            open-on-focus
            @input='sendUpdate'>
          </b-taginput>
        </b-field>

        <b-field>
          <template #label>
            <span class='label'>Financially At Least As Good On...</span>
          </template>

          <b-taginput
            v-model='selectedMetrics'
            :data='metrics'
            autocomplete
            field='name'
            open-on-focus
            @input='sendUpdate'>
          </b-taginput>
        </b-field>
      </div>
      

      <div class='has-text-centered mt-5 mb-3'>
        <b-button
          class='is-dark is-large is-fullwidth serif'  
          @click='requestResults'
          :loading='loading'>
          Generate Portfolio
        </b-button>
      </div>

      <b-notification
        v-model='error.active'
        type='is-danger'
        auto-close
        :duration='2000'>
        {{ error.messages[error.active] }}
      </b-notification>
    </div>



    <b-collapse animation='slide' :open='false'>
      <template #trigger>
        <p class='mx-5 mb-2 is-size-7 has-text-link'>Advanced options</p>
      </template>

      <div class='card-footer p-5 is-flex-direction-column'>
        <div class='input-row mb-5'>
          <CheckboxButtonSelect
            label='Asset Types'
            :options='assetTypes'
            :default='selectedAssetTypes'
            @input='updateAssetTypes'
          />

          <CheckboxButtonSelect
            label='ESG Mandate'
            :options='esgOptions'
            :default='esgSelectedOptions'
            @input='updateESG'
          />
        </div>


        <div class='input-row'>
          <b-field class='border-override'>
            <template #label>
              <span class='label'>Share Class (optional)</span>
            </template>

            <b-autocomplete
              v-model='selectedShareClass'
              :data='shareClasses'
              open-on-focus
              clearable
              @select='updateShareClass'>
            </b-autocomplete>
          </b-field>

          <b-field class='border-override'>
            <template #label>
              <span class='label'>Minimum Engagement Intensity</span>
            </template>

            <b-autocomplete
              v-model='selectedEngagement'
              :data='engagementLevels'
              field='label'
              open-on-focus
              @select='updateEngagement'>
            </b-autocomplete>
          </b-field>
        </div>


        <div class='input-row'>
          <b-field class='border-override'>
            <template #label>
              <span class='label'>Allowed Fund Universe (optional)</span>
            </template>

            <b-autocomplete
              v-model='universeQuery'
              :data='universes'
              field='name'
              :loading='universesLoading'
              open-on-focus
              clearable
              @select='selectUniverse'>

              <template slot-scope='props'>
                <span>{{ props.option.name }}</span>
                <i v-if="props.option.portfolio_type === 'shareowner'" class='fas fa-user ml-1'></i>
              </template>
            </b-autocomplete>
          </b-field>
        </div>
      </div>
    </b-collapse>
  </div>
</template>



<script>
  import RadioButtonSelect    from './RadioButtonSelect.vue'
  import CheckboxButtonSelect from './CheckboxButtonSelect.vue'
  
  export default {
    name: 'design-portfolio-inputs',
    components: { RadioButtonSelect, CheckboxButtonSelect },
    props: ['loading'],
    data() { return {
      urls: {
        portfolios:   '/api/v1/portfolios/?type=modelportfolio&asset_types=2,3',
        shareClasses: '/api/v1/taxonomy/share_classes/null', // Should remove '/null' after new api route is available
      },
      showUniverseInput: false,
      defaultUniverses: [],
      universeQuery: '',
      universesLoading: false,
      universes: [],
      selectedUniverse: null,

      defaultPortfolios: [],
      portfolioQuery: '',
      portfoliosLoading: false,
      portfolios: [],
      selectedPortfolio: null,
      
      valueSets: [],
      filteredValueSets: [],
      selectedValues: [],

      selectedMetrics: [],
      metrics: [
        { name: '5y Returns',             value: 'financial_data.Load Adjusted Return 5Y Month-End' },
        { name: '5y Sharpe',              value: 'financial_data.Sharpe Ratio 5Y Total Mo-End' },
        { name: '5y Beta',                value: 'financial_data.Beta 5Y Total Mo-End' },
        { name: '5y Alpha',               value: 'financial_data.Alpha 5Y Total Mo-End' },
        { name: '5y Standard Deviation',  value: 'financial_data.Standard Deviation 5Y Total Mo-End - Base Currency' },
        { name: '5y R Squared',           value: 'financial_data.R-Squared 5Y Total Mo-End' },
        { name: 'Expense Ratio',          value: 'ticker__expense_ratio' },
        { name: 'Shareholder Engagement', value: 'ticker__engagement_level' },
      ],

      selectedAssetTypes: [2, 3],
      assetTypes: [
        { value: 2, label: 'ETFs' },
        { value: 3, label: 'Mutual Funds' }
      ],

      esgSelectedOptions: ['y', 'n'],
      esgOptions: [
        { value: 'y', label: 'Yes' },
        { value: 'n', label: 'No' },
      ],

      selectedEngagement: 'Base',
      engagementLevels: [
        { value: 0, label: 'Minimal' },
        { value: 1, label: 'Base' },
        { value: 2, label: 'Deep' },
      ],

      shareClasses: [],
      selectedShareClass: null,

      error: {
        active: null,
        messages: {
          MISSING_FIELDS: 'Select a starting portfolio and at least one cause area',
          MISSING_TYPES:  'Select at least one asset type'
        }
      }
    }},
    beforeCreate() {},
    created() {
      this.debouncedSearchUniverses  = _.debounce(this.searchUniverses,  250)
      this.debouncedSearchPortfolios = _.debounce(this.searchPortfolios, 250)
    },
    mounted() {
      this.getDefaultUniverses()
      this.getDefaultPortfolios()
      this.getValueSets()
      this.getShareClasses()
    },
    updated() {},
    computed: {
      engagementByName() {
        const byName = {};

        this.engagementLevels.forEach(level => {
          byName[level.label] = level.value;
        });
        return byName;
      }
    },
    watch: {
      universeQuery: function(newQuery, oldQuery) {
        if(!newQuery) return this.resetUniverses()
        this.universesLoading = true
        this.debouncedSearchUniverses()
      },
      portfolioQuery: function(newQuery, oldQuery) {
        if(!newQuery) return this.resetPortfolios()
        this.portfoliosLoading = true
        this.debouncedSearchPortfolios()
      }
    },
    methods: {
      toggleUniverse() {
        this.showUniverseInput = !this.showUniverseInput
      },
      async getDefaultUniverses() {
        const res  = await fetch(this.urls.portfolios)
        const data = await res.json()

        this.defaultUniverses = this.removeEmptyPortfolios(data.portfolios)
        this.universes = this.defaultUniverses
      },
      resetUniverses() {
        this.universes = this.defaultUniverses
        this.universesLoading = false
      },
      async searchUniverses() {
        if(!this.universeQuery) return this.resetUniverses()
        const res  = await fetch(this.urls.portfolios + '&search=' + this.universeQuery)
        const data = await res.json()

        this.universes = this.removeEmptyPortfolios(data.portfolios)
        this.universesLoading = false
      },
      selectUniverse(universe) {
        this.selectedUniverse = universe
        this.sendUpdate()
      },

      async getDefaultPortfolios() {
        const res  = await fetch(this.urls.portfolios)
        const data = await res.json()

        this.defaultPortfolios = this.removeEmptyPortfolios(data.portfolios)
        this.portfolios = this.defaultPortfolios
      },
      resetPortfolios() {
        this.portfolios = this.defaultPortfolios
        this.portfoliosLoading = false
      },
      async searchPortfolios() {
        if(!this.portfolioQuery) return this.resetPortfolios()
        const res  = await fetch(this.urls.portfolios + '&search=' + this.portfolioQuery)
        const data = await res.json()

        this.portfolios = this.removeEmptyPortfolios(data.portfolios)
        this.portfoliosLoading = false
      },
      selectPortfolio(portfolio) {
        this.selectedPortfolio = portfolio
        this.sendUpdate()
      },

      removeEmptyPortfolios(portfolios) {
        const filtered = portfolios.filter(p => p.has_holdings)
        return [...filtered]
      },

      async getValueSets() {
        const res  = await fetch('/api/v1/screen_sets/')
        const data = await res.json()

        this.valueSets = data.screen_sets
        this.filteredValueSets = this.valueSets
      },
      getFilteredValueSets(query) {
        const q = query.toLowerCase()

        if(q.length === 1) {
          this.filteredValueSets = this.valueSets.filter(v => {
            return v.name[0].toLowerCase() === q
          })
          return
        }

        this.filteredValueSets = this.valueSets.filter(v => {
          return v.name.toLowerCase().includes(q)
        })
      },

      updateAssetTypes(assetTypes) {
        this.selectedAssetTypes = assetTypes
        this.sendUpdate()
      },

      updateEngagement(level) {
        this.selectedEngagement = level.label;
        this.sendUpdate();
      },

      updateESG(options) {
        this.esgSelectedOptions = options;
        this.sendUpdate();
      },
      getESG() {
        const selected = this.esgSelectedOptions;

        if(selected.length === 1) {
          const esg = selected[0];
          if(esg === 'y') return true;
          if(esg === 'n') return false;
        }
        return null;
      },

      async getShareClasses() {
        const res  = await fetch(this.urls.shareClasses)
        const data = await res.json()

        this.shareClasses = data.share_classes.filter(cls => cls?.trim())
      },
      updateShareClass(shareClass) {
        this.selectedShareClass = shareClass;
        this.sendUpdate();
      },
      
      sendUpdate() {
        const universe  = this.selectedUniverse
        const portfolio = this.selectedPortfolio

        const p = ({ id, name }) => {
          return { id, name }
        }

        this.$emit('updated', {
          universe:   universe  ? universe.id  : null,
          portfolio:  portfolio ? p(portfolio) : null,
          values:     this.selectedValues.map( v => v.id),
          metrics:    this.selectedMetrics.map(m => m.value),
          engagement: this.engagementByName[this.selectedEngagement],
          assetTypes: this.selectedAssetTypes,
          esgMandate: this.getESG(),
          shareClass: this.selectedShareClass,
        })
      },
      requestResults() {
        if(!this.hasFields()) {
          this.error.active = 'MISSING_FIELDS'
          return
        }

        if(!this.hasAssetTypes()) {
          this.error.active = 'MISSING_TYPES'
          return
        }

        this.$emit('resultsRequested')
      },
      hasFields() {
        return (
          this.selectedPortfolio &&
          this.selectedValues && this.selectedValues.length > 0
        )
      },
      hasAssetTypes() {
        if(!this.selectedAssetTypes) return false
        return this.selectedAssetTypes.length > 0
      }
    }
  }
</script>



<style scoped>
  .input-row {
    display: flex;
    gap:     2rem;
  }

  .input-row > * {
    flex-basis: 50%;
    flex-grow:  1;
  }
</style>



<style>
  #design-portfolio-inputs .border-override .input {
    border: 1px solid #dbdbdb !important;
  }
</style>